import { createFileRoute, useRouter } from "@tanstack/react-router";
import { motion } from "framer-motion";

import createAxios from "@/lib/axios";
import { Card, CardContent } from "@/components/ui/card";
import Markdown from "@/components/Markdown";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PencilIcon, RefreshCcwIcon, SaveIcon } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Spinner } from "@/components/Spinner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { usePeriodicRefresh } from "@/hooks/usePeriodicRefresh";

type QuestionMutation = {
  question: string;
  options?: string[];
  answer: string;
};

type Question = {
  type: string;
  topic_id: number;
} & QuestionMutation;

type QuestionSet = {
  _id: string;
  progress: {
    message: "DONE" | string;
    timestamp: string;
  }[];
  questions: Question[];
  updatedAt: string;
};

export const Route = createFileRoute("/dashboard/quiz/$id")({
  component: QuestionsCarousel,
  loader: async ({ params: { id } }) => {
    const client = await createAxios();
    const { data } = await client.get<QuestionSet>(
      `${import.meta.env.VITE_BACKEND_URL}/qa-set/${id}`,
    );

    return data;
  },
});

function QuestionsCarousel() {
  const { questions, progress } = Route.useLoaderData();
  if (!progress || progress.length === 0) {
    return <ProgressView message="Generating..." />;
  }
  const lastProgress = progress[progress.length - 1];
  const inProgress = lastProgress && lastProgress.message !== "DONE";
  if (inProgress) {
    return <ProgressView message={lastProgress.message} />;
  }
  return (
    <div className="flex h-full w-full flex-col gap-4 p-10">
      <div className="flex items-center justify-between pb-4">
        <h1 className="text-2xl font-bold">View Generated Quiz</h1>
        <Button
          variant="default"
          className="bg-cyan-400"
          onClick={() => (window.location.href = "/dashboard")}
        >
          Go to Home
        </Button>
      </div>
      {questions.map((question, idx) => (
        <Question key={idx} idx={idx} question={question} />
      ))}
    </div>
  );
}

// show only latest progress message and the time data was last fetched
function ProgressView({ message }: { message: string }) {
  usePeriodicRefresh(5000);
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex w-full max-w-2xl flex-col items-center gap-8">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="flex flex-row items-center gap-3">
            <Spinner />
            <span className="text-lg font-medium text-foreground">
              {message}
            </span>
          </div>
          <div className="relative mt-2 h-3 w-full overflow-hidden rounded-full border border-cyan-700 bg-foreground shadow-sm">
            <motion.div
              className="absolute left-0 top-0 h-full w-2/5 rounded-full bg-cyan-800 shadow-md"
              initial={{ x: "-100%" }}
              animate={{ x: ["-100%", "300%"] }}
              transition={{
                repeat: Infinity,
                duration: 1.2,
                ease: "easeInOut",
                repeatType: "loop",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Question({ idx, question }: { idx: number; question: Question }) {
  const router = useRouter();
  const { _id } = Route.useLoaderData();
  const [isEditing, setIsEditing] = useState(false);

  const [questionState, setQuestionState] =
    useState<QuestionMutation>(question);

  const { mutate: handleRegenerate, isPending: qaEntryIsRegenerating } =
    useMutation({
      mutationKey: ["qa-set", _id, "regenerate", idx],
      mutationFn: async () => {
        const client = await createAxios();
        await client.post(
          import.meta.env.VITE_BACKEND_URL + `/qa-set/${_id}/regenerate/${idx}`,
        );
        await router.invalidate();
        setIsEditing(false);
      },
    });

  const { mutate: handleSave, isPending: qaEntryIsSaving } = useMutation({
    mutationKey: ["qa-set", _id, "save", idx],
    mutationFn: async (body: QuestionMutation) => {
      body;
      const client = await createAxios();
      await client.post(
        import.meta.env.VITE_BACKEND_URL + `/qa-set/${_id}/update/${idx}`,
        body,
        { headers: { "Content-Type": "application/json" } },
      );
      await router.invalidate();
      setIsEditing(false);
    },
  });
  if (qaEntryIsRegenerating || qaEntryIsSaving) {
    return (
      <Card className="relative w-full">
        <div className="space-y-2 p-4">
          <Skeleton className="h-20 w-4/5 bg-gray-300" />
          <Skeleton className="h-10 w-3/5 bg-gray-300" />
          <Skeleton className="h-44 w-full bg-gray-300">
            <div className="flex size-full flex-col items-center justify-center">
              <Spinner />
              <p className="mt-2 text-sm text-gray-500">
                {qaEntryIsRegenerating
                  ? "Regenerating..."
                  : qaEntryIsSaving
                    ? "Saving..."
                    : ""}
              </p>
            </div>
          </Skeleton>
        </div>
      </Card>
    );
  }
  return (
    <>
      <Card>
        {isEditing ? (
          <EditQuestionAnswer
            questionState={questionState}
            setQuestionState={setQuestionState}
            handleRegenerate={handleRegenerate}
            handleSave={() => handleSave(questionState)}
          />
        ) : (
          <ViewQuestionAnswer
            idx={idx}
            questionState={questionState}
            editCallback={() => setIsEditing(true)}
          />
        )}
      </Card>
    </>
  );
}

function ViewQuestionAnswer({
  idx,
  questionState: { question, answer, options },
  editCallback,
}: {
  idx: number;
  questionState: QuestionMutation;
  editCallback: () => void;
}) {
  const isOptionAnswerMatch = options?.includes(answer);
  const selectedColors = {
    background: "bg-purple-100",
    border: "border-purple-500 text-purple-600",
  };
  return (
    <CardContent className="pt-6">
      <div className="flex w-full flex-row items-start justify-between gap-2">
        <div className="w-full">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row">
              <span className="text-3xl font-semibold">{idx + 1}.&nbsp;</span>
              <Markdown>{question}</Markdown>
            </div>
            {options && (
              <RadioGroup className="pl-6">
                {options.map((opt, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-row items-center gap-2 rounded p-2 ${opt === answer ? selectedColors.background : ""
                      }`}
                  >
                    <RadioGroupItem
                      key={idx}
                      className={`border-secondary ${opt === answer ? selectedColors.border : ""
                        }`}
                      value={opt}
                      checked={opt === answer}
                      disabled
                    />
                    <span className="text-base">{opt}</span>
                  </div>
                ))}
              </RadioGroup>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-1">
          <Button
            variant={"outlinecard"}
            size={"icon"}
            onClick={() => editCallback()}
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {/* only render detailed answer if not already shown by selecting the matching option */}
      {!isOptionAnswerMatch && (
        <>
          <Seperator />
          <div className="w-full">
            <Markdown>{answer}</Markdown>
          </div>
        </>
      )}
    </CardContent>
  );
}

function EditQuestionAnswer({
  questionState,
  setQuestionState,
  handleRegenerate,
  handleSave,
}: {
  questionState: QuestionMutation;
  setQuestionState: (question: QuestionMutation) => void;
  handleRegenerate: () => void;
  handleSave: () => void;
}) {
  const { question, answer, options } = questionState;
  return (
    <div className="p-4">
      <div className="w-full">
        <textarea
          value={question}
          className="h-48 w-full rounded-md bg-gray-100 p-4"
          onChange={(e) =>
            setQuestionState({ ...questionState, question: e.target.value })
          }
          autoFocus
        />
        {options && (
          <RadioGroup className="pl-6">
            {options.map((opt, idx) => (
              <div key={idx} className="flex flex-row items-center gap-2">
                <RadioGroupItem
                  key={idx}
                  className="border-secondary"
                  value={opt}
                  disabled
                />
                <Input
                  className="bg-gray-200 text-base"
                  value={opt}
                  onChange={(e) =>
                    setQuestionState({
                      ...questionState,
                      options: [
                        ...options.slice(0, idx),
                        e.target.value,
                        ...options.slice(idx + 1),
                      ],
                    })
                  }
                />
              </div>
            ))}
          </RadioGroup>
        )}
        <Seperator />
        <textarea
          value={answer}
          className="h-48 w-full rounded-md bg-gray-100 p-4"
          onChange={(e) =>
            setQuestionState({ ...questionState, answer: e.target.value })
          }
        />
      </div>
      <EditPanel handleRegenerate={handleRegenerate} handleSave={handleSave} />
    </div>
  );
}

function EditPanel({
  handleRegenerate,
  handleSave,
}: {
  handleRegenerate: () => void;
  handleSave: () => void;
}) {
  return (
    <div className="flex flex-row justify-between">
      <div></div>
      <div className="flex flex-row gap-1">
        <Button variant={"outlinecard"} onClick={handleRegenerate}>
          <RefreshCcwIcon className="mr-2 h-4 w-4" />
          <span>Regenerate</span>
        </Button>
        <Button variant={"secondary"} onClick={handleSave}>
          <SaveIcon className="mr-2 h-4 w-4" />
          <span>Save</span>
        </Button>
      </div>
    </div>
  );
}

function Seperator() {
  return <div className="my-2 h-[0.05rem] w-full rounded-full bg-black"></div>;
}
